import { NAVIGATION_REGISTRY, type NavContext, type NavEntry } from "@/app/auth/navigation.registry";
import { findEntryByTo, isEntryVisible } from "@/app/auth/resolveNavAccess";
import type { GlobalTranslations } from "@/shared/lang/interfaces/global";

import {
  VERIFICATION_WELCOME_FRESH_DAYS,
  VERIFICATION_WELCOME_MAX_SHORTCUTS,
  VERIFICATION_WELCOME_STORAGE_PREFIX,
} from "../constants/verification.constants";
import type { AccountVerificationResponse } from "../types/verification-api.types";

// ─── Welcome after approval ───────────────────────────────────────────────────
// Pure decisions behind the "you can use the platform now" welcome: which shortcuts it offers, when the
// dashboard shows it, and the per-browser mark that keeps it to once. Kept out of the components so the rules
// are tested without a DOM, the same split `accountVerification.memo` makes.

/** Where onboarding lives; the welcome renders inline there, so the dashboard never stacks a dialog on top. */
const ONBOARDING_ROUTE_PREFIX = "/app/onboarding";

/**
 * Settings → Security, where the second factor that guards money movements is set up. The same path as
 * `TWO_FACTOR_SETTINGS_PATH`, spelled out here so this file stays free of component imports.
 */
const SECURITY_SETTINGS_HREF = "/app/settings?section=seguridad";

export type VerificationWelcomeShortcutId =
  | "payins"
  | "payouts"
  | "cards"
  | "wallets"
  | "security"
  | "crypto"
  | "payout_batches"
  | "clients";

export interface VerificationWelcomeShortcutSpec {
  id: VerificationWelcomeShortcutId;
  /** The registry entry the shortcut opens: it is offered only when that entry is visible to the user. */
  entryTo: string;
  /** Where it navigates. The entry's own `to`, except where a section anchor says more. */
  href: string;
  /** The sidebar's label, so a destination keeps one name everywhere. `null` when the sidebar has none. */
  labelKey: keyof GlobalTranslations | null;
}

/**
 * Candidates in the order they are offered: money in and out first (what verification unlocks), then the
 * rest. Every gate is the registry's — tenancy, RBAC, the organization's module list and feature flags — so a
 * shortcut can never open a page that would answer with `NoAccessView`.
 */
export const VERIFICATION_WELCOME_SHORTCUTS: readonly VerificationWelcomeShortcutSpec[] = [
  { id: "payins", entryTo: "/app/payins", href: "/app/payins", labelKey: "sidebar_menu_payins" },
  { id: "payouts", entryTo: "/app/dispersions", href: "/app/dispersions", labelKey: "sidebar_menu_payouts" },
  { id: "cards", entryTo: "/app/cards/cards", href: "/app/cards/cards", labelKey: "sidebar_menu_cards" },
  { id: "wallets", entryTo: "/app/wallets", href: "/app/wallets", labelKey: "sidebar_menu_wallets_home" },
  // Only while no second factor is enrolled: every money movement asks for one, and finding that out at the
  // first payout is the worst moment to.
  { id: "security", entryTo: "/app/settings", href: SECURITY_SETTINGS_HREF, labelKey: null },
  {
    id: "crypto",
    entryTo: "/app/crypto-swaps",
    href: "/app/crypto-swaps",
    labelKey: "sidebar_menu_crypto_swaps",
  },
  {
    id: "payout_batches",
    entryTo: "/app/payout-batches",
    href: "/app/payout-batches",
    labelKey: "sidebar_menu_payout_batches",
  },
  { id: "clients", entryTo: "/app/clients", href: "/app/clients", labelKey: "sidebar_menu_clients" },
];

/**
 * The shortcuts this user may open, in offer order, capped at {@link VERIFICATION_WELCOME_MAX_SHORTCUTS}.
 *
 * `isEntryVisible`, not `isEntryRoutable`: a shortcut to a page the sidebar does not show would drop the user
 * somewhere with no menu item, the reasoning `resolveLandingPath` applies too.
 *
 * `excludeHref` leaves out the page the welcome's one primary button already opens (the landing page), so the
 * same destination is never offered twice; it is applied before the cap, so the choices stay full.
 */
export function resolveVerificationWelcomeShortcuts(
  ctx: NavContext | null,
  options: { twoFactorEnabled: boolean; excludeHref?: string | null },
  registry: readonly NavEntry[] = NAVIGATION_REGISTRY,
): VerificationWelcomeShortcutSpec[] {
  if (!ctx) return [];
  return VERIFICATION_WELCOME_SHORTCUTS.filter((spec) => {
    if (spec.id === "security" && options.twoFactorEnabled) return false;
    if (options.excludeHref && spec.href === options.excludeHref) return false;
    const entry = findEntryByTo(spec.entryTo, registry);
    return entry !== undefined && isEntryVisible(entry, ctx);
  }).slice(0, VERIFICATION_WELCOME_MAX_SHORTCUTS);
}

export interface VerificationWelcomeCandidateInput {
  isOrgMember: boolean;
  data: AccountVerificationResponse | null;
  now: number;
  pathname: string;
}

/**
 * Should the dashboard announce this organization's approval? Only to its members, only for a VERIFIED
 * corporate identity approved within {@link VERIFICATION_WELCOME_FRESH_DAYS}, and never on the onboarding
 * route, which shows the same welcome inline.
 *
 * A VERIFIED identity without `verified_at` is treated as old: the backend stamps it on every approval, so
 * only rows that predate the stamp lack it, and those organizations were approved long ago.
 */
export function isVerificationWelcomeCandidate({
  isOrgMember,
  data,
  now,
  pathname,
}: VerificationWelcomeCandidateInput): boolean {
  if (!isOrgMember || !data) return false;
  if (data.kyc_status !== "VERIFIED" || !data.identity_id || !data.verified_at) return false;
  if (pathname === ONBOARDING_ROUTE_PREFIX || pathname.startsWith(`${ONBOARDING_ROUTE_PREFIX}/`))
    return false;

  const verifiedAt = Date.parse(data.verified_at);
  if (Number.isNaN(verifiedAt)) return false;
  return now - verifiedAt <= VERIFICATION_WELCOME_FRESH_DAYS * 24 * 60 * 60 * 1000;
}

/** One mark per user AND corporate identity: switching identity and getting approved again is news again. */
export function verificationWelcomeKey(userId: string, identityId: string): string {
  return `${VERIFICATION_WELCOME_STORAGE_PREFIX}${userId}:${identityId}`;
}

export type VerificationWelcomeStorage = Pick<Storage, "getItem" | "setItem">;

// Marks made while storage refused them (private mode, blocked site data): the welcome still shows once per
// session instead of on every navigation.
const marksInMemory = new Set<string>();

function browserStorage(): VerificationWelcomeStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function hasSeenVerificationWelcome(
  key: string,
  storage: VerificationWelcomeStorage | null = browserStorage(),
): boolean {
  if (marksInMemory.has(key)) return true;
  try {
    return storage?.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function markVerificationWelcomeSeen(
  key: string,
  storage: VerificationWelcomeStorage | null = browserStorage(),
): void {
  marksInMemory.add(key);
  try {
    storage?.setItem(key, "1");
  } catch {
    // The in-memory mark above covers this session.
  }
}
